import type { SiteLanguage } from "@/utils/routing";
import { CREW, CREW_ORDER, WORLD_PAGES, type CrewSet, type World, type WorldPage } from "./crewData";

/** Opening and closing quotation marks, e.g. ["„", "“"] or ["“", "”"]. */
export type QuoteMarks = readonly [string, string];

export const SK_QUOTES: QuoteMarks = ["„", "“"];

/** A spoken line in the language's quotation marks. */
export const quoted = (text: string, marks: QuoteMarks = SK_QUOTES): string => `${marks[0]}${text}${marks[1]}`;

/** Rotating quotes and two plain points shown on each home-page door. */
export interface DoorLines {
  quotes: string[];
  points: string[];
}

/**
 * Everything the home-page block (crew line-up and the two doors) needs in one
 * language. Each country writes its own: the split, the lines and the trades
 * follow local customs, not a translation of the Slovak copy.
 */
export interface WorldsContent extends CrewSet {
  lang: SiteLanguage;
  /** Opening and closing quotation marks around the spoken lines; Slovak „…“ when left out. */
  quotes?: QuoteMarks;
  pages: Record<World, WorldPage>;
  doors: Record<World, DoorLines>;
  ui: {
    heading: string;
    subheading: string;
    lineupAria: string;
    doorsBadge: string;
    doorsTitle: string;
    doorsIntro: string;
    doorLabel: string;
    doorCta: string;
    doorsFooter: string;
  };
}

export const SK_WORLDS: WorldsContent = {
  lang: "sk",
  crew: CREW,
  order: CREW_ORDER,
  pages: WORLD_PAGES,
  doors: {
    survive: {
      quotes: [
        "Vzal som to pod cenu. Leasing nepočká.",
        "Na výplaty zase nevyšlo.",
        "Lacnejší ma prebil o tretinu.",
        "Zarobené mám. Peniaze ešte nie.",
      ],
      points: [
        "Beriete aj zákazky pod cenu, lebo leasing a výplaty nepočkajú.",
        "Peniaze máte zarobené, len ešte neprišli.",
      ],
    },
    grow: {
      quotes: [
        "Robotu mám na pol roka. Nestíham.",
        "Dvaja na PN-ke a stavba stojí.",
        "Materiál mizne a neviem kam.",
        "Obrat rastie, na účte je menej.",
      ],
      points: [
        "Ľudí pribúda a s nimi dovolenky, PN-ky a straty.",
        "Obrat rastie, a na účte je aj tak menej.",
      ],
    },
  },
  ui: {
    heading: "Ktorý z nich ste vy?",
    subheading: "Kliknite na toho, kto je vám najbližšie, a pozrite sa, kam ďalej.",
    lineupAria: "Vyberte, kto je vám najbližšie",
    doorsBadge: "Povedzme si to úprimne",
    doorsTitle: "Kde ste dnes?",
    doorsIntro:
      "Väčšina ľudí, čo robia rukami, žije z mesiaca na mesiac. Niektorí majú roboty toľko, že nestíhajú. Ťažké je oboje, len inak.",
    doorLabel: "Dvere",
    doorCta: "Toto som ja",
    doorsFooter: "Ste niekde medzi? To je väčšina. Pozrite si obe.",
  },
};
